import { useEffect, useState } from "react";
import { Loader2, Send, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Reusable editable email composer for invoices / quotations / work-orders / payment emails.
// cfg: { endpoint, title, defaultTo, defaultSubject, defaultMessage, attachmentLabel, submitLabel }
export function EmailComposeDialog({ cfg, onClose, onSent }) {
  const open = !!cfg;
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (cfg) {
      setTo(cfg.defaultTo || "");
      setSubject(cfg.defaultSubject || "");
      setMessage(cfg.defaultMessage || "");
    }
  }, [cfg]);

  const submit = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const res = await api(cfg.endpoint, {
        method: "POST",
        body: JSON.stringify({ to: to.trim(), subject: subject.trim() || undefined, message: message.trim() || undefined }),
      });
      if (res.email_delivered) toast.success(`Emailed to ${res.to}`);
      else if (!res.email_configured) toast.success(`Prepared for ${res.to} (email activates once deployed)`);
      else if (res.status === "duplicate_skipped") toast.success(`Already sent to ${res.to} moments ago`);
      else toast.success(`Queued for ${res.to}`);
      onSent?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Could not send email");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="email-compose-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{cfg?.title || "Send email"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">RECIPIENT</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to" type="email" className="w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand" /></label>
          <label className="block"><span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">SUBJECT</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className="w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand" /></label>
          <label className="block"><span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">MESSAGE</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="email-message" rows={5} placeholder="Add a personal note (optional)" className="w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand" /></label>
          {cfg?.attachmentLabel && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="email-attachment-note"><Paperclip className="h-3.5 w-3.5" />{cfg.attachmentLabel} will be attached as a PDF</p>
          )}
          <button onClick={submit} disabled={sending} data-testid="email-send-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}{cfg?.submitLabel || "SEND EMAIL"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
